/**
 * Preview Console-Capture Script (debug log — browser capture half).
 *
 * Injected into an author/editor HTML preview iframe. Captures the signal the
 * mini-app debug log needs from the browser side — which nothing captured
 * before — and ships it to the parent via postMessage:
 *   - console.log / info / debug / warn / error
 *   - window.onerror + unhandledrejection (uncaught errors)
 *   - failed fetch() / XMLHttpRequest (status ≥ 400 or network error)
 *   - a form submission no handler prevented (#1002 — the page is about to navigate)
 *   - a per-load `session_start` marker (edit/version context)
 *
 * The parent (HtmlViewer) forwards batches to POST /api/debug-log/:kind/:id,
 * which redacts + dedups + retains (see ../core/debug-log-core.ts). We ALSO
 * redact + truncate HERE (defence at source) so a secret never leaves the
 * iframe in a postMessage in the first place.
 *
 * ⚠️ MUST STAY IMPORT-FREE — same rule as preview-snapshot.ts. Exposed via the
 * client-safe `@papan/files/api/console-script` subpath and bundled into the WEB
 * app (HtmlViewer injects it into the inline srcDoc). Any import here would pull
 * server code (hono / @papan/database / node:crypto) into the client bundle. The
 * whole module body is a template string. If it ever needs a dependency, move it
 * to a client-safe package first.
 */

/**
 * Returns the console-capture bridge as a `<script>` string to inject before
 * </body>. Two delivery modes:
 *   - default (author/editor preview): posts `papan:console` batches to the
 *     PARENT frame — the HtmlViewer receiver decides whether to persist.
 *   - `ingestPath` (ppn.gg App-mode share): the app runs TOP-LEVEL (no parent
 *     receiver exists), so batches POST directly to the same-origin ingest
 *     endpoint instead (allowed by the app CSP's connect-src 'self'; the BFF
 *     cookie authenticates the share session server-side). Uses the NATIVE
 *     fetch captured before our own fetch-wrapper installs, so a failing
 *     ingest POST can never feed back into the capture loop.
 */
export function getConsoleCaptureScript(opts?: { ingestPath?: string; parentOrigin?: string }): string {
  return `<script data-papan-console>
(function(){
  if (window.__papanConsoleCapture) return; // idempotent (double-inject guard)
  window.__papanConsoleCapture = true;

  // ─── Delivery target: parent postMessage (default) or direct POST ───
  var INGEST = ${JSON.stringify(opts?.ingestPath ?? '')};
  var RAW_FETCH = window.fetch ? window.fetch.bind(window) : null;

  // ─── Parent-origin targeting (same pattern as the snapshot bridge) ──
  // #1408: learned only from Papan — the parent window, and on the app's own preview origin Papan's
  // exact origin (passed by the server) — so a frame the app embeds can't redirect the log stream.
  var PAPAN_PARENT = ${JSON.stringify(opts?.parentOrigin ?? '')};
  var parentOrigin = PAPAN_PARENT || '*';
  window.addEventListener('message', function(e){
    if (!e || e.source !== window.parent || PAPAN_PARENT) return;
    if (e.origin && e.origin !== 'null') parentOrigin = e.origin;
  });

  // ─── Per-load session id + caps ─────────────────────────────────────
  var SESS = (Date.now().toString(36) + Math.random().toString(36).slice(2, 8));
  // #385: expose THIS load's session id so the co-injected snapshot/eval bridge
  // can hand it back to the agent's preview tools. That lets preview_screenshot /
  // preview_eval read errors captured for exactly this load (not a stale earlier
  // one the agent already fixed, and not a race where "latest" is still the prior
  // run mid-reload).
  try { window.__papanConsoleSess = SESS; } catch (e) {}
  var MAX_FIELD = 2048;          // per-field char cap
  var FLUSH_AT = 20;             // entries per batch
  var FLUSH_MS = 2000;           // idle flush interval
  var RATE_PER_SEC = 50;         // sampling: max entries/sec
  var SESSION_CAP = 500;         // hard per-load entry cap
  var buffer = [];
  var totalThisSession = 0;
  var windowStart = Date.now();
  var windowCount = 0;
  var suppressedNote = false;
  var flushTimer = null;

  // ─── Redaction at source (compact mirror of the server redactor) ────
  var RED = '\\u2039redacted\\u203a'; // ‹redacted›
  function redact(s){
    try {
      return String(s)
        .replace(/\\b(bearer\\s+)[A-Za-z0-9._~+/-]+=*/gi, '$1' + RED)
        .replace(/\\beyJ[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+/g, RED)
        .replace(/\\beyJ[A-Za-z0-9_-]{16,}\\.[A-Za-z0-9_-]{16,}/g, RED)
        .replace(/\\bpst1_[A-Za-z0-9_-]{16,}/g, RED)
        .replace(/((?:authorization|token|secret|password|passwd|pwd|api[_-]?key|access[_-]?token|refresh[_-]?token|client[_-]?secret|session[_-]?id|sig|signature)["']?\\s*[:=]\\s*["']?)([^"'\\s&,;}]+)/gi, '$1' + RED)
        .replace(/\\b[A-Fa-f0-9]{32,}\\b/g, RED)
        .replace(/\\b[A-Za-z0-9+]{40,}={0,2}\\b/g, RED);
    } catch (_e) { return ''; }
  }
  function clamp(s){
    var r = redact(s);
    if (r.length <= MAX_FIELD) return r;
    return r.slice(0, MAX_FIELD) + '\\u2026(+' + (r.length - MAX_FIELD) + ' chars)';
  }

  // ─── Format console args → a single string ──────────────────────────
  function fmt(args){
    var out = [];
    for (var i = 0; i < args.length; i++){
      var a = args[i];
      if (a instanceof Error) { out.push(a.message); continue; }
      if (typeof a === 'object' && a !== null){
        try { out.push(JSON.stringify(a)); } catch (_e) { out.push(Object.prototype.toString.call(a)); }
      } else { out.push(String(a)); }
    }
    return out.join(' ');
  }

  // ─── Platform-noise filter ──────────────────────────────────────────
  // SDK/platform-internal console output (e.g. '[papan realtime] Realtime
  // unavailable — N reconnect attempts…', emit-cap warnings) is NOT app signal;
  // it would just pollute the app's debug log. Drop it from capture — it still
  // reaches the REAL browser console via the original console method below.
  function isPlatformNoise(msg){
    if (typeof msg !== 'string') return false;
    // Carve-out (4.3.3): throttle warnings ARE app signal — the author (and the build-app
    // agent iterating on a generated app) must see that the app is being rate/concurrency
    // limited. Whitelist them by the throttle-warn MESSAGE SHAPE (scripts.run refused: code)
    // — NOT a hardcoded list of error codes, which silently went stale when a new limit code
    // was added (see packages/sdk/src/throttle-warn.ts; #269 review, cleanup 7). Any code that
    // flows through warnIfThrottled carries this shape. Throttled to 1/min/script.
    if (/^\\[papan\\] scripts\\.run "[^"]*" refused:/.test(msg)) return false;
    return /^\\[papan[\\s\\]]/.test(msg);
  }

  // ─── Buffering + flushing ───────────────────────────────────────────
  function flush(){
    if (flushTimer){ clearTimeout(flushTimer); flushTimer = null; }
    if (!buffer.length) return;
    var entries = buffer;
    buffer = [];
    if (INGEST && RAW_FETCH){
      // keepalive so the pagehide flush survives navigation/close.
      try { RAW_FETCH(INGEST, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ entries: entries }), keepalive: true }).catch(function(){}); } catch (_e) { /* best-effort */ }
      return;
    }
    try { parent.postMessage({ type: 'papan:console', sess: SESS, entries: entries }, parentOrigin); } catch (_e) { /* parent gone */ }
  }
  function schedule(){
    if (buffer.length >= FLUSH_AT){ flush(); return; }
    if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_MS);
  }
  function push(entry, urgent){
    // Session hard cap.
    if (totalThisSession >= SESSION_CAP){
      if (!suppressedNote){
        suppressedNote = true;
        buffer.push({ ts: Date.now(), sess: SESS, src: 'meta', event: 'suppressed', msg: 'session entry cap reached (' + SESSION_CAP + ')' });
        flush();
      }
      return;
    }
    // Per-second rate limit (sampling).
    var now = Date.now();
    if (now - windowStart >= 1000){ windowStart = now; windowCount = 0; }
    if (windowCount >= RATE_PER_SEC){
      if (!suppressedNote){
        suppressedNote = true;
        buffer.push({ ts: now, sess: SESS, src: 'meta', event: 'suppressed', msg: 'log rate limit hit (' + RATE_PER_SEC + '/s)' });
      }
      return;
    }
    windowCount++;
    suppressedNote = false;
    totalThisSession++;
    entry.ts = entry.ts || now;
    entry.sess = SESS;
    // Which page produced this — the parent (HtmlViewer) maps the raw pathname
    // to a path relative to the app root so a multi-file miniapp's consolidated
    // log can attribute entries per file. Robust across in-app navigation:
    // every page load reinjects the shim with its own location.
    try { entry.page = (location && location.pathname) || ''; } catch (_e) {}
    buffer.push(entry);
    if (urgent) flush(); else schedule();
  }

  // ─── console.* overrides ────────────────────────────────────────────
  var LEVELS = ['log','info','debug','warn','error'];
  for (var k = 0; k < LEVELS.length; k++){
    (function(level){
      var orig = console[level];
      console[level] = function(){
        // Flush notable levels (warn/error) immediately so they show without the
        // batch delay; log/info stay buffered to avoid a POST per line. SDK/
        // platform-internal lines are dropped from capture (still logged to the
        // real console via orig below).
        try { var m = clamp(fmt(arguments)); if (!isPlatformNoise(m)) push({ src: 'browser', lvl: level, msg: m }, level === 'error' || level === 'warn'); } catch (_e) { /* never break the app */ }
        if (orig) try { orig.apply(console, arguments); } catch (_e) {}
      };
    })(LEVELS[k]);
  }

  // ─── Uncaught errors ────────────────────────────────────────────────
  window.addEventListener('error', function(e){
    try {
      var msg = e && e.message ? e.message : 'Uncaught error';
      var stack = e && e.error && e.error.stack ? clamp(e.error.stack) : undefined;
      var loc = e && e.filename ? (' (' + e.filename + ':' + (e.lineno||0) + ':' + (e.colno||0) + ')') : '';
      push({ src: 'browser', lvl: 'error', msg: clamp(msg + loc), stack: stack }, true);
    } catch (_e) {}
  });
  window.addEventListener('unhandledrejection', function(e){
    try {
      var r = e ? e.reason : null;
      var msg = (r && r.message) ? r.message : String(r);
      var stack = (r && r.stack) ? clamp(r.stack) : undefined;
      push({ src: 'browser', lvl: 'error', msg: clamp('Unhandled rejection: ' + msg), stack: stack }, true);
    } catch (_e) {}
  });

  // ─── Failed network (fetch + XHR) ───────────────────────────────────
  if (window.fetch){
    var origFetch = window.fetch;
    window.fetch = function(input, init){
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      return origFetch.apply(this, arguments).then(function(res){
        if (res && res.status >= 400){
          push({ src: 'browser', lvl: 'network', msg: clamp('fetch ' + res.status + ' ' + url), url: clamp(url), status: res.status }, true);
        }
        return res;
      }, function(err){
        push({ src: 'browser', lvl: 'network', msg: clamp('fetch failed ' + url + ': ' + (err && err.message || err)), url: clamp(url) }, true);
        throw err;
      });
    };
  }
  if (window.XMLHttpRequest){
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url){ this.__papanUrl = url; return origOpen.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function(){
      var xhr = this;
      xhr.addEventListener('loadend', function(){
        try {
          if (xhr.status === 0){
            push({ src: 'browser', lvl: 'network', msg: clamp('xhr failed ' + (xhr.__papanUrl||'')), url: clamp(xhr.__papanUrl||'') }, true);
          } else if (xhr.status >= 400){
            push({ src: 'browser', lvl: 'network', msg: clamp('xhr ' + xhr.status + ' ' + (xhr.__papanUrl||'')), url: clamp(xhr.__papanUrl||''), status: xhr.status }, true);
          }
        } catch (_e) {}
      });
      return origSend.apply(this, arguments);
    };
  }

  // ─── Form submissions nobody handled (#1002) ────────────────────────
  // Forms are allowed in the app frame, so a page whose submit handler threw
  // before preventDefault(), or never called it, is about to navigate away
  // (with no action: reload itself) and lose its state. Nothing blocks it; this
  // says why the page just reset.
  //
  // WHEN it decides: in a task AFTER dispatch (setTimeout 0). In the editor
  // preview this shim is injected first in <head> (pd.app: before </body>), so
  // a page listener on window itself (delegated submit, Alpine
  // \`@submit.window.prevent\`) can run after ours — a verdict read
  // inside our listener would blame a page that did prevent it. But a
  // preventDefault() that arrives AFTER dispatch (after an \`await\` in the
  // handler, with requestSubmit()) is too late — the browser already planned
  // the navigation — so only calls made DURING dispatch count: the event's
  // preventDefault is wrapped here to tell them apart by eventPhase.
  // The navigation is a queued task and the old document lives until the
  // response, so the warning lands before the page goes.
  //
  // WHAT it skips, because nothing navigates: untrusted (script-dispatched)
  // submit events, method="dialog", and a form already removed from the
  // document when our window listener runs (a disconnected form cannot
  // navigate; one a LATER window listener removes still gets a warning). Not seen at all:
  // forms in shadow DOM (submit isn't composed) and handlers that stop
  // propagation — those are missed warnings, not wrong ones.
  //
  // Form properties are read through the PROTOTYPE: a control named
  // \`action\` / \`target\` / \`method\` shadows them on the instance.
  // \`form.submit()\` fires no event and is the author's explicit choice.
  var FP = window.HTMLFormElement && HTMLFormElement.prototype;
  var IN_FRAME = (function(){ try { return window.top !== window; } catch (_e) { return true; } })();
  function formGet(form, prop){
    try { var d = Object.getOwnPropertyDescriptor(FP, prop); return d && d.get ? d.get.call(form) : undefined; } catch (_e) { return undefined; }
  }
  function hasAttr(el, name){
    try { return !!el && Element.prototype.hasAttribute.call(el, name); } catch (_e) { return false; }
  }
  function attrOf(el, name){
    try { return Element.prototype.getAttribute.call(el, name); } catch (_e) { return null; }
  }
  function warnUnprevented(form, s, lateCall){
    try {
      var method = String(hasAttr(s, 'formmethod') ? attrOf(s, 'formmethod') : (attrOf(form, 'method') || '')).toLowerCase();
      if (method === 'dialog') return; // closes a <dialog>; no navigation
      var action = hasAttr(s, 'formaction') ? s.formAction : formGet(form, 'action');
      // A PRESENT attribute wins even when empty (empty = _self); only an
      // absent one falls back to <base target>.
      var target = hasAttr(s, 'formtarget') ? attrOf(s, 'formtarget')
        : hasAttr(form, 'target') ? attrOf(form, 'target')
        : (function(){ var b = document.querySelector('base[target]'); return b ? attrOf(b, 'target') : ''; })();
      var rawTarget = String(target || '');
      target = rawTarget.toLowerCase();
      // A name equal to this window's own name targets THIS page (names are
      // case-sensitive, hence the raw value).
      // A valid browsing-context name never starts with '_' (those are keywords).
      var ownName = ''; try { ownName = window.name || ''; } catch (_e) {}
      if (ownName.charAt(0) === '_') ownName = '';
      // Only a submission that replaces THIS page loses state. Another window
      // or frame is a deliberate non-JS form (a payment button, a search
      // opening a results tab) — nothing to warn about.
      // Only http(s) replaces the page: javascript: runs in place; mailto: /
      // tel: / sms: hand off to another app (blocked in the sandboxed frame).
      if (action && !/^\\s*https?:/i.test(String(action))) return;
      var framedTop = (target === '_top' || target === '_parent') && IN_FRAME;
      var self = !target || target === '_self' || (!!ownName && rawTarget === ownName) || ((target === '_top' || target === '_parent') && !IN_FRAME);
      if (!self && !framedTop) return;
      var where = self
        ? '; this page will navigate away and lose its state'
        : ' (target ' + target + ' — it navigates the enclosing page; inside the Papan app frame it is blocked)';
      var hint = lateCall
        ? ' preventDefault() was called only after the event finished (after an await?) — that is too late; call it first, synchronously.'
        : framedTop
          ? ' To open another site from the app, use target="_blank"; if this form is handled in JavaScript, call event.preventDefault() first in its submit handler.'
          : ' If this form is handled in JavaScript, call event.preventDefault() first in its submit handler (an error thrown before it also lets the form submit), and give a button inside the form that is not its submit button type="button". A form meant to go to another site needs neither.';
      // Logged without query or fragment: an absent / empty / "#" / "?" action
      // resolves to the document URL WITH its query (a GET self-submit put the
      // visitor's last input there), and a GET submission replaces the query
      // with the form data anyway.
      var dest = String(action || location.href).split('#')[0].split('?')[0];
      push({ src: 'browser', lvl: 'warn', msg: clamp('Form submitted to ' + dest + where + '.' + hint) }, true);
    } catch (_e) {}
  }
  window.addEventListener('submit', function(e){
    try {
      if (!e || !e.isTrusted || !FP) return;
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      // Decided NOW, during dispatch: what navigates is fixed by the end of it.
      if (!form.isConnected) return;
      var s = e.submitter || null;
      var during = e.defaultPrevented; // a form/document listener already prevented it
      var late = false;
      var orig = e.preventDefault;
      try {
        e.preventDefault = function(){
          if (e.eventPhase !== 0) during = true;
          // After dispatch: already canceled (by a path we didn't see) means it
          // was canceled in time — this late call is redundant, not too late.
          else { if (e.defaultPrevented) during = true; late = true; }
          return orig.apply(e, arguments);
        };
      } catch (_e) {}
      setTimeout(function(){
        // A cancel the wrapper didn't see (\`returnValue = false\`, a handler
        // returning false, a frozen Event.prototype, a saved prototype
        // reference) still shows in defaultPrevented: trust it unless the only
        // call we saw was a late one.
        if (during || (e.defaultPrevented && !late)) return;
        warnUnprevented(form, s, late);
      }, 0);
    } catch (_e) {}
  });

  // ─── Session marker + flush-on-hide ─────────────────────────────────
  // Urgent flush (#385): the marker lands promptly so a preview tool querying
  // for THIS load's session id sees it settle in ~one network hop rather than
  // waiting out the idle-flush timer (which would otherwise leave a clean reload
  // looking like the previous, possibly-broken, run).
  push({ src: 'meta', event: 'session_start', ua: clamp(navigator.userAgent || '') }, true);
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function(){ if (document.visibilityState === 'hidden') flush(); });
})();
</script>`
}
